import * as React from 'react';
import * as shallowEqual from 'shallowequal';
import * as _ from 'underscore';

import { GraphQLTagValuesQuery } from '../../../catalog/fetchers/GraphQLTagValuesQuery';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { FilterPanelTypeaheadScope, IFilterPanelContainerScope } from '../../../ts/controllers/FilterPanelCtrl';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { Filter } from '../../../ts/helpers/Filter';
import { ServerFilterClauses } from '../../../ts/helpers/ServerFilterClauses';
import { ServerApiMapper } from '../../../ts/helpers/ServerModelHelper';
import { ExplorableItemDefinitions } from '../../../ts/models/ContentDefinitions';
import { ContentFilterKeyName, ContentFiltersKeys } from '../../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { FlowOutputStepRunContentType, MixedContentType } from '../../../ts/models/ContentTypes';
import { FieldValuesQuery } from '../../../ts/resources/FieldValuesQuery';
import { FlowOutputStepRuns } from '../../../ts/resources/FlowOutputStepRuns';
import { GetFieldValuesForMixedContentRequest, GetFieldValuesRequest } from '../../../ts/ServerApi/requests/GetFieldValuesRequest';
import { FilterFieldNames, IFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { FilterPanelService, IFilterDefinition } from '../../../ts/services/FilterPanelService';
import { ServerService } from '../../../ts/services/ServerService';
import { getFilterClausesForTypes, removeFilterClauseFromFilters } from '../../actions/spaces/filters/FilterHelpers';
import { ServiceLocator } from '../../ServiceLocator';
import { FilterMap } from '../../state/ContentExplorerState';
import { isBackgroundJobsSpace, isFavoritesSpace, isFlowSpace, isRecentsSpace, isSearchSpace, isSiteSpace, isSiteUserSpace, isSomeKindaSearchSpace, SpaceInfo, TopLevelProjectId, isAllCollectionsSpace, isSiteUserCollectionsSubspace } from '../../state/SpaceInfo';

export interface AdvancedFilterPanelProps {
  readonly angularContext: AngularContext;
  readonly mixedContentFilterMap: FilterMap;
  readonly spaceInfo: SpaceInfo;
  readonly shownContentTypes: ReadonlyArray<ExplorableItemType>;
  readonly scopeReference?: (scope: AdvancedFilterPanelScope) => void;
}

const template = `<div ng-controller="FilterPanelCtrl" ng-include="'advancedFilterPanel.html'"></div>`;

export type AdvancedFilterPanelScope = IFilterPanelContainerScope & FilterPanelTypeaheadScope & { contentType: string };

export class AdvancedFilterPanel extends React.Component<AdvancedFilterPanelProps> {

  public static TestId = 'advanced-filter-panel';

  private filterPanelService: FilterPanelService;
  private serverService: ServerService;
  private filterPanelScope: AdvancedFilterPanelScope = null;

  private static FilterKeysForTopLevelOnly = [
    ContentFiltersKeys.project,
    ContentFiltersKeys.allProjects
  ];

  public constructor(props: AdvancedFilterPanelProps) {
    super(props);

    // We ought to not bring in an Angular service if we can help it ... but it may be a necessary evil
    this.filterPanelService = this.props.angularContext.getService('FilterPanelService');
    this.serverService = this.props.angularContext.getService('ServerService');
  }

  public componentWillReceiveProps(nextProps: AdvancedFilterPanelProps): void {
    // We need to update the scope whenever our showContentTypes are updated to make sure
    // we render the correct filters in the angular pane
    // TODO future optimizations: Should we move this into render? Should we memoize its results?
    // Can this be a pure component after all?
    if (!shallowEqual(nextProps.shownContentTypes, this.props.shownContentTypes) ||
        !shallowEqual(nextProps.spaceInfo, this.props.spaceInfo)) {
      _.defer(() => this.filterPanelScope.$apply((scope: AdvancedFilterPanelScope) => {
        scope.filterDefinitions = this.getAdvancedFiltersForShownContentTypes();
        scope.getFieldValueQuery = this.getFieldValueQuery();
        scope.contentType = (nextProps.shownContentTypes.length === 1) ? nextProps.shownContentTypes[0] : MixedContentType.name;
        // TFSID: 1114216 - Re-enable search for collection space
        scope.hideSearch = this.shouldHideSearchForSpaceInfo();
      }));
    }
  }

  public componentDidMount() {
    if (this.props.scopeReference) {
      this.props.scopeReference(this.filterPanelScope);
    }
  }

  private isShowingOneContentType(): boolean {
    return this.props.shownContentTypes.length === 1;
  }

  private isShowingTableContent(): boolean {
    return this.props.shownContentTypes.every(t => t === 'table');
  }

  private isShowingDatabaseContent(): boolean {
    return this.props.shownContentTypes.every(t => t === 'database');
  }

  private getAdvancedFiltersForShownContentTypes(): IFilterDefinition<any>[] {
    let filterKeys: ContentFilterKeyName[];

    if (this.isShowingOneContentType()) {
      filterKeys = ExplorableItemDefinitions[this.props.shownContentTypes[0]].contentFilterKeys;
    } else {
      // no types or multiple types - show mixed content filters
      filterKeys = MixedContentType.filterKeys;
      if (!isRecentsSpace(this.props.spaceInfo)) {
        // only recents page supports the tags filter for mixed content
        filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.tag);
      }
    }

    if (isBackgroundJobsSpace(this.props.spaceInfo)) {
      // The showSystemJobs filter is only available for server admin users
      if (!this.serverService.isSessionServerAdmin()) {
        filterKeys = _.without(filterKeys, 'showSystemJobs');
      }
    }

    // If not at top level (so project place), remove the top-level-only keys
    if (!isSiteSpace(this.props.spaceInfo)) {
      filterKeys = _.reject(filterKeys, (key) => _.contains(AdvancedFilterPanel.FilterKeysForTopLevelOnly, key));
    }

    // remove favorites filter when on favorites space
    if (isFavoritesSpace(this.props.spaceInfo)) {
      filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.favorite);
    }

    // remove recents filter when on recents space
    if (isRecentsSpace(this.props.spaceInfo)) {
      filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.recent);
    }

    if (isFlowSpace(this.props.spaceInfo)) {
      filterKeys = FlowOutputStepRunContentType.filterKeys;
    }

    if (isSiteUserSpace(this.props.spaceInfo)) {
      filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.owner);
    }

    return this.filterPanelService.getFilterDefinitions(filterKeys);
  }

  private getFieldValueQueryForShownContentTypes(filterField: string): any {
    const fetcherFactory = this.props.angularContext.getService('FetcherFactory');

    if (this.isShowingDatabaseContent()) {
      return new GraphQLTagValuesQuery('database');
    } else if (this.isShowingTableContent()) {
      return new GraphQLTagValuesQuery('table');
    }

    if (this.isShowingOneContentType()) {
      const fieldValuesFetcher = fetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
      const resourceType = ExplorableItemDefinitions[this.props.shownContentTypes[0]].fieldValueResourceType;
      return new FieldValuesQuery(fieldValuesFetcher, filterField, resourceType);
    } else {
      // use mixed content api
      const fieldValuesFetcher = fetcherFactory.getServerResourceFetcher(GetFieldValuesForMixedContentRequest, ServerApiMapper.fieldValues);
      return new FieldValuesQuery(fieldValuesFetcher, filterField);
    }
  }

  private getFieldValueQuery(): (filterField: string) => FieldValuesQuery {
    return (filterField: string) => {
      if (this.props.spaceInfo.subspaceName === 'runHistory') {
        let filterClauses: IFilterClause[] = _.chain(this.props.mixedContentFilterMap)
                                              .pick(FlowOutputStepRunContentType.filterKeys)
                                              .values()
                                              .compact()
                                              .value();

        filterClauses = removeFilterClauseFromFilters(filterClauses, FilterFieldNames.flowOutputStepName);
        filterClauses.push(Filter.stringFieldEq(FilterFieldNames.flowId, this.props.spaceInfo.item.id));

        const flowOutputStepRuns: FlowOutputStepRuns = ServiceLocator.get('FlowOutputStepRuns');
        return flowOutputStepRuns.valuesForField(FilterFieldNames.flowOutputStepName).withFilters(filterClauses);
      }

      let filtersForType: IFilterClause[] = getFilterClausesForTypes(
          this.props.mixedContentFilterMap,
          this.props.shownContentTypes,
          this.props.shownContentTypes,
          isSiteUserSpace(this.props.spaceInfo) ? TopLevelProjectId : this.props.spaceInfo.item.id,
          this.props.spaceInfo
      );

      // need to add the project filter clause so we don't get everything on the site/server for mixed content
      if (!this.isShowingOneContentType()) {
        const projectIdFilter = ServerFilterClauses.serverFilterClauses.project(this.props.spaceInfo.item.id);
        filtersForType.push(projectIdFilter);
      }

      // add the favorites=true filter when in favorites space
      if (isFavoritesSpace(this.props.spaceInfo)) {
        const favoritesFilter = ServerFilterClauses.serverFilterClauses.favorite('true');
        filtersForType.push(favoritesFilter);

      } else if (isRecentsSpace(this.props.spaceInfo)) {
        const recentsFilter = ServerFilterClauses.serverFilterClauses.recent('true');
        filtersForType.push(recentsFilter);
      }

      // if we are searching for projects or in the site - projects space we want to get field values for all projects not just top level ones
      if (isSomeKindaSearchSpace(this.props.spaceInfo) && this.props.shownContentTypes.length === 1 && this.props.shownContentTypes[0] === 'project') {
        filtersForType = removeFilterClauseFromFilters(filtersForType, FilterFieldNames.topLevelProject);
      }

      if (isSiteSpace(this.props.spaceInfo) && this.props.spaceInfo.subspaceName === 'project') {
        filtersForType = removeFilterClauseFromFilters(filtersForType, FilterFieldNames.topLevelProject);
      }

      if (isSiteUserSpace(this.props.spaceInfo)) {
        filtersForType = removeFilterClauseFromFilters(filtersForType, FilterFieldNames.topLevelProject);
        filtersForType.push(ServerFilterClauses.serverFilterClauses.owner(this.props.spaceInfo.item.id));
      }

      // We want to remove the field we are looking for so we can get all the possibilities back for it.
      // If the filter has no field, it obviously does not match the current field we want, so keep it.
      const filterClausesWithoutQueriedField = removeFilterClauseFromFilters(filtersForType, filterField);

      const fieldValuesQuery: FieldValuesQuery = this.getFieldValueQueryForShownContentTypes(filterField);
      return fieldValuesQuery.withFilters(filterClausesWithoutQueriedField).withMatchText('');
    };
  }

  private readonly setupScopeForFilterPanel = (scope: ng.IScope): AdvancedFilterPanelScope => {
    const childScope = scope as AdvancedFilterPanelScope;
    this.filterPanelScope = childScope;

    childScope.filterDefinitions = this.getAdvancedFiltersForShownContentTypes();
    childScope.filterControls = {};

    childScope.getFieldValueQuery = this.getFieldValueQuery();

    childScope.contentType = this.isShowingOneContentType() ? this.props.shownContentTypes[0] : MixedContentType.name;

    // TFSID: 1114216 - Re-enable search for collection space
    childScope.hideSearch = this.shouldHideSearchForSpaceInfo();

    return childScope;
  }

  private shouldHideSearchForSpaceInfo(): boolean {
    return isSomeKindaSearchSpace(this.props.spaceInfo) || isBackgroundJobsSpace(this.props.spaceInfo)
      || isAllCollectionsSpace(this.props.spaceInfo) || isSiteUserCollectionsSubspace(this.props.spaceInfo);
  }

  public render(): JSX.Element {
    return (
      <AngularDirective template={template}
        angularContext={this.props.angularContext}
        setupScope={this.setupScopeForFilterPanel}
      />
    );
  }
}
